"use client";

import {
  cancelFrame,
  type FrameData,
  frame,
  type MotionValue,
  useMotionValue,
} from "motion/react";
import { type RefObject, useCallback, useEffect, useRef } from "react";
import {
  idlePose,
  type SpringState,
  stepSpring,
} from "@/components/wallet/sticker-motion";
import { useDeviceTilt } from "@/components/wallet/use-device-tilt";

/**
 * The CD case's "it's a real object" motion, borrowed from the wallet
 * sticker showcase (`sticker-motion.ts`, `use-device-tilt.ts`) at a smaller
 * amplitude: a slow idle yaw/pitch/float plus the phone's own tilt, sprung.
 *
 * One `frame.update` loop, alive only while the case is on screen, the page
 * is visible and `enabled` (active slide, no reduced motion, no low power) —
 * a profile parked by Activity (`display: none`) reports not intersecting
 * and stops it. Disabled, every value settles to 0 and nothing runs.
 */

/** Fraction of the sticker's idle drift (13° yaw / 5° pitch / 8px float). */
const IDLE_SCALE = 0.4;
/** Degrees a full device tilt (≈18°) turns the case. */
const TILT_YAW_DEG = 9;
const TILT_PITCH_DEG = 6;

export function useCaseDrift({
  targetRef,
  enabled,
}: {
  targetRef: RefObject<Element | null>;
  enabled: boolean;
}): {
  yaw: MotionValue<number>;
  pitch: MotionValue<number>;
  floatY: MotionValue<number>;
} {
  const yaw = useMotionValue(0);
  const pitch = useMotionValue(0);
  const floatY = useMotionValue(0);
  const gyro = useRef({ x: 0, y: 0 });

  const onTilt = useCallback((x: number, y: number) => {
    gyro.current = { x, y };
  }, []);
  useDeviceTilt(targetRef, enabled, onTilt);

  useEffect(() => {
    const element = targetRef.current;
    if (!enabled || !element) {
      yaw.set(0);
      pitch.set(0);
      floatY.set(0);
      return;
    }

    let visible = false;
    let running = false;
    let seconds = 0;
    let springX: SpringState = { value: 0, velocity: 0 };
    let springY: SpringState = { value: 0, velocity: 0 };

    const tick = ({ delta }: FrameData) => {
      const dt = Math.min(delta / 1000, 1 / 20);
      seconds += dt;
      springX = stepSpring(springX, gyro.current.x, dt, 90, 14);
      springY = stepSpring(springY, gyro.current.y, dt, 90, 14);
      const idle = idlePose(seconds, IDLE_SCALE);
      yaw.set(idle.yaw + springX.value * TILT_YAW_DEG);
      pitch.set(idle.pitch - springY.value * TILT_PITCH_DEG);
      // Same float range as the stickers, scaled: centred on 0.
      floatY.set((idle.lift - 0.5) * 8 * IDLE_SCALE * -1);
    };

    const sync = () => {
      const want = visible && !document.hidden;
      if (want === running) return;
      running = want;
      if (want) frame.update(tick, true);
      else cancelFrame(tick);
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      sync();
    });
    observer.observe(element);
    document.addEventListener("visibilitychange", sync);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      cancelFrame(tick);
    };
  }, [enabled, floatY, pitch, targetRef, yaw]);

  return { yaw, pitch, floatY };
}
